// React imports
import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

// Generic imports
import axios from 'axios';
import _ from 'lodash';
import dayjs from 'dayjs';
import * as graph from '@microsoft/microsoft-graph-client';

// MUI imports
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import LoadingButton from '@mui/lab/LoadingButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { red, yellow } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';

// Fontawesome import
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruckFast, faFloppyDisk, faCheck, faTrash, faClone, faHandHoldingDollar,
     faEnvelope, faCircleQuestion, faEllipsis, faUser, faLock, faUnlock, faXmark,
     faStar, faGear} from '@fortawesome/pro-light-svg-icons';
import { faStar as faStarFilled } from '@fortawesome/pro-solid-svg-icons';

// Local imports
import Header from '../../Header.js';
import readableId from '../../ReadableId.js';
import EmailConversation from '../emailpage/EmailPage.conversation';
import email from '../utils/email.js';
import OwnerFilter from '../components/OpportunitiesFilter.owner.js';
import { DUMMY_CUSTOMER_EMAIL } from './OpportunityPreviewAndSendBase.js';
import AdvancedSettingsDialog from './OpportunityOneOffBuilder.advancedsettings.js';
import FollowUpDialog from '../../FollowUpDialog.js';
import LostReasonDialog from '../components/LostReasonDialog.js';
import { lostReasonText } from '../../../schema/lostReasons.js';

function OpportunityHeader(props) {
    const { 
        org, activeStep, onNextLoading, onNextDisabled, saveDisabled, saveSuccess, status, account, opportunity = {},
        saving, saveError, setSaveError, saveErrorDescription, onSave, onDuplicate, onDuplicateDisabled, onDelete, onNextStep, onPrevStep,
        accesstoken, onUpdateOpportunity, setMessage, onReopen, me, owner, onTakeOwnership, minimizedSearch,
        tags, setTags, possibleTags, tagsError, emailLoginInfo, lockedExchangeRates, setLockedExchangeRates,
        onEmailRefreshConnection, onEmailLogout,
        _graph = graph,
    } = props;
    const { accounts } = useOutletContext() || {};
    const [ showFollowUp, setShowFollowUp ] = useState(false);
    const [ followUpToast, setFollowUpToast ] = useState(null);
    // The header's own error toast: the screens don't all pass setMessage, and calling a missing
    // one crashed the page ("N is not a function") when marking Won/Lost failed.
    const [ errorToast, setErrorToast ] = useState(null);
    const showError = message => (typeof setMessage === 'function' ? setMessage({ open: true, severity: 'error', message }) : setErrorToast(message));
    // Same rule as the status menu and the server: only the owner (or Monada support) marks Won/Lost.
    const canChangeStatus = !me?.email || me.email === owner || me.email === 'admin@monada.ai';
    const statusError = (e, what) => (e?.response?.status === 401
        ? `Only the quote's owner${owner ? ` (${owner})` : ''} can mark it as ${what}. Ask them, or take ownership of the quote first.`
        : `Couldn't mark the quote as ${what}${e?.response?.data?.detail ? `: ${e.response.data.detail}` : ''}. Please try again.`);
    const [ markingCompleted, setMarkingCompleted ] = useState(false);
    const [ markingLost, setMarkingLost ] = useState(false);
    const [ askLostReason, setAskLostReason ] = useState(false);
    const [ deleteRequest, setDeleteRequest ] = useState(false);
    const [ foundThread, setFoundThread ] = useState(null);
    const [ showThread, setShowThread ] = useState(false);
    const [ changeOwnership, setChangeOwnership ] = useState(false);
    const [ hideErrors, setHideErrors ] = useState(false);
    const [ advancedSettingsOpen, setAdvancedSettingsOpen ] = useState(false);
    const theme = useTheme();

    const navigate = useNavigate();

    const rejection = (((opportunity || {}).log || []).findLast(l => l.action === 'rejected') || {});

    useEffect(() => {
        setHideErrors(false);
    }, [saveErrorDescription]);

    async function onMarkCompleted() {
        setMarkingCompleted(true);

        try {
            const { data } = await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${opportunity.organizationId}&accesstoken=${accesstoken}`, {
                status: 'completed',
            });
            onUpdateOpportunity({ opportunity: {...opportunity, ...data }});
            setMarkingCompleted(false);
        } catch (e) {
            showError(statusError(e, 'Won'));
            setMarkingCompleted(false);
        }
    }

    async function onMarkLost(lostReason) {
        setMarkingLost(true);

        try {
            const { data } = await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${opportunity.organizationId}&accesstoken=${accesstoken}`, {
                status: 'lost',
                lostReason: _.omit(lostReason, 'offerId'),
                ...(lostReason?.offerId ? { lostOffer: lostReason.offerId } : {}),
            });
            onUpdateOpportunity({ opportunity: {...opportunity, ...data }});
            setMarkingLost(false);
            setAskLostReason(false);
        } catch (e) {
            setAskLostReason(false);
            showError(statusError(e, 'Lost'));
            setMarkingLost(false);
        }
    }

    async function onShowThread() {
        setShowThread(true);
    }

    async function onLockExchangeRates() {
        setLockedExchangeRates(!lockedExchangeRates);
    }

    useEffect(() => {
        if (!emailLoginInfo || emailLoginInfo?.status !== 'connected' || opportunity?.source?.type !== 'email') return;
        
        (async () => {
            try {
                const messages = await email.getMessagesForConversation({ org, conversationId: opportunity.source.id, tokens: emailLoginInfo.tokens });
                if (!messages || messages.length === 0) {
                    setFoundThread('not-owner');
                    return;
                }

                setFoundThread(messages);

            } catch (e) {
                // Check if this is an expired token error
                const errorMessage = e?.message || e?.error?.message || JSON.stringify(e);
                const isExpiredError = errorMessage?.includes('Lifetime validation failed') || 
                                      errorMessage?.includes('expired') ||
                                      errorMessage?.includes('InvalidAuthenticationToken') ||
                                      e?.statusCode === 401 ||
                                      e?.code === 'InvalidAuthenticationToken';
                
                if (isExpiredError) {
                    // Token expired - silently handle, the expired listener will be called by getMessagesForConversation
                    setFoundThread('not-owner');
                } else {
                    setFoundThread('not-owner');
                    console.error(e);
                }
            }
        })();

    }, [opportunity, emailLoginInfo?.status]);

    let toRecipients = [];
    let ccRecipients = [];

    // Helper to parse recipients from log entry (checks both 'reason' and 'details' fields)
    const parseRecipientsFromLog = (log) => {
        if (!log) return { toRecipients: [], ccRecipients: [] };
        const data = log.reason || log.details;
        if (!data) return { toRecipients: [], ccRecipients: [] };
        try {
            const parsed = JSON.parse(data);
            return {
                toRecipients: parsed.toRecipients || [],
                ccRecipients: parsed.ccRecipients || []
            };
        } catch (e) {
            return { toRecipients: [], ccRecipients: [] };
        }
    };

    if (status === 'sending-to-endcustomer') {
        const log = opportunity.log.findLast(l => l.action === 'sending-to-endcustomer');
        const parsed = parseRecipientsFromLog(log);
        toRecipients = parsed.toRecipients;
        ccRecipients = parsed.ccRecipients;
    } else if (status === 'sent-to-endcustomer' || status === 'opened-by-customer') {
        // Try 'sent-to-endcustomer' log first, then fall back to 'sending-to-endcustomer'
        let log = opportunity.log.findLast(l => l.action === 'sent-to-endcustomer');
        let parsed = parseRecipientsFromLog(log);
        if (parsed.toRecipients.length === 0) {
            log = opportunity.log.findLast(l => l.action === 'sending-to-endcustomer');
            parsed = parseRecipientsFromLog(log);
        }
        toRecipients = parsed.toRecipients;
        ccRecipients = parsed.ccRecipients;
    } else if (status === 'completed' || status === 'endcustomer-accepted') {
        const log = opportunity.log.findLast(l => l.action === 'accepted');
        if (log && log.customerEmail) {
            // Filter out dummy/skipped customer information
            if (log.customerEmail !== DUMMY_CUSTOMER_EMAIL) {
                toRecipients = [{ address: log.customerEmail, name: log.customerName }];
            }
        } else if (opportunity.account?.email) {
            toRecipients = [{ address: opportunity.account.email, name: opportunity.account.name }];
        }
    }

    return (
        <>
            <Header
                fixed
                width={minimizedSearch ? 'calc(100% - 273px)' : 'calc(100% - 800px)'}
                onBack={() => navigate(-1)}
                icon={opportunity && opportunity.id === opportunity.organizationId ? faHandHoldingDollar : faTruckFast}
                title={opportunity && opportunity.id === opportunity.organizationId ? 'Company Tariff' : (account || {}).name || 'Opportunity'}
                buttons={opportunity && opportunity.id === opportunity.organizationId ? [{
                    title: 'Save',
                    icon: saveSuccess ? faCheck : faFloppyDisk,
                    color: saveSuccess ? 'success' : 'monada-button',
                    action: onSave,
                    disabled: saveDisabled || (status === 'deleted'),
                    thinking: saving,
                }] : _.compact([{
                    title: 'Save',
                    icon: saveSuccess ? faCheck : faFloppyDisk,
                    color: saveSuccess ? 'success' : 'monada-button',
                    action: onSave,
                    disabled: saveDisabled || (status === 'deleted'),
                    thinking: saving,
                    tooltipShow: !_.isEmpty(saveErrorDescription) && !hideErrors,
                    tooltip: !_.isEmpty(saveErrorDescription) && !hideErrors ? (
                        <Box sx={{ padding: '0px 12px' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px' }}>
                                <IconButton size='small' color='inherit' onClick={() => setHideErrors(true)}>
                                    <FontAwesomeIcon icon={faXmark} />
                                </IconButton>
                            </Box>
                            {saveErrorDescription?.map(e => (
                                <Alert severity='error' key={e.id} sx={{ marginBottom: '12px' }}>
                                    {e.message}
                                </Alert>
                            ))}
                        </Box>
                    ) : null,
                }, {
                    icon: faEllipsis,
                    submenu: _.compact([opportunity?.source?.type === 'email' ? {
                        title: ((!foundThread && emailLoginInfo?.status === 'connected') || !emailLoginInfo) ? <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '70px' }}><CircularProgress size={14} /></Box> :
                            'Show Thread',
                        icon: faEnvelope,
                        color: 'monada-button',
                        action: onShowThread,
                        disabled: !onShowThread || !foundThread || foundThread === 'not-owner' || emailLoginInfo?.status !== 'connected',
                        tooltip: foundThread === 'not-owner' ? 'You are not the owner of this thread' :
                            emailLoginInfo && emailLoginInfo?.status !== 'connected' ? 'You are not connected to email. Please use the Email screen to connect.' : null,
                    } : null, {
                        title: 'Duplicate',
                        icon: faClone,
                        color: 'monada-button',
                        action: onDuplicate,
                        // Only gate Duplicate on unsaved edits for users who can actually save
                        // (the owner or an admin). A non-owner can never clear the dirty state —
                        // their save is blocked by the owner-only guard — so gating them here is a
                        // dead-end. Duplicate copies the saved state into a brand-new opportunity
                        // anyway, so it's safe to let non-owners duplicate while dirty.
                        disabled: onDuplicateDisabled && (opportunity?.owner === me.email || me.email === 'admin@monada.ai'),
                        tooltip: (onDuplicateDisabled && (opportunity?.owner === me.email || me.email === 'admin@monada.ai')) ? 'Save your changes to enable duplicate' : null,
                    }, opportunity?.owner === me.email ? {
                        title: 'Change ownership',
                        icon: faUser,
                        action: () => setChangeOwnership(true),
                    } : null, {
                        title: 'divider',
                    }, {
                        title: lockedExchangeRates ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                                Unlock Exchange Rates
                                <Typography variant='caption' sx={{ fontSize: '11px', color: theme.palette.text.secondary }}>
                                    (locked {dayjs(lockedExchangeRates.locked).format('L LT')})
                                </Typography>
                            </Box>
                        ) : 'Lock Exchange Rates',
                        icon: lockedExchangeRates ? faLock : faUnlock,
                        action: () => onLockExchangeRates(),
                    }, {
                        title: 'divider-2',
                    }, {
                        title: 'Advanced Settings',
                        icon: faGear,
                        action: () => setAdvancedSettingsOpen(true),
                    }, {
                        title: 'divider-3',
                    }, {
                        title: 'Delete',
                        icon: faTrash,
                        iconColor: red[500],
                        color: 'error',
                        action: () => setDeleteRequest(true),
                    }]),
                }])}
                subtitle={opportunity && opportunity.id === opportunity.organizationId ? null : (
                    <Autocomplete
                        className={'nonotch'}
                        clearIcon={false}
                        options={_.xorBy(possibleTags, tags, 'label')}
                        filterOptions={(options, state) => {
                            const { inputValue } = state;
                            if (inputValue.length < 1) return [];
                            return _.sortBy(options.filter(a => a.label?.toLowerCase().startsWith(inputValue.toLowerCase())), a => a.label);
                        }}
                        freeSolo
                        multiple
                        renderTags={(value, props) =>
                            value.map((option, index) => (
                                <Chip label={option.label} {...props({ index })} />
                            ))
                        }
                        value={tags || []}
                        onChange={(e, value) => {
                            const tags = value.map(a => typeof(a) === 'string' ? ({ label: a }) : a);
                            setTags({ tags });
                        }}
                        renderInput={(params) => <TextField placeholder='Start typing to add tags...' sx={{ border: 0 }} InputProps={{ sx: { border: 0 }}} inputProps={{ style: { border: 0 }}} {...params} />}
                    />
                )}
            />
            {tagsError && (
                <Alert severity='error' sx={{ marginBottom: '12px' }}>
                    <Typography variant='caption'>Error saving tags. Please try again later.</Typography>
                </Alert>
            )}

            {deleteRequest && (
                <Alert 
                    severity={'error'} 
                    action={
                        <>
                            <Button color='inherit' size='small' onClick={() => {
                                onDelete();
                                setDeleteRequest(null);
                            }}>
                                Yes
                            </Button>
                            <Button color='inherit' size='small' onClick={() => setDeleteRequest(null)}>
                                No
                            </Button>
                        </>
                    }
                >
                    Are you sure you want to delete this opportunity?
                </Alert>
            )}

            {saveError && (
                <Alert severity={'error'} onClose={() => setSaveError(null)}>
                    {saveError}
                </Alert>
            )}

            {status === 'deleted' && (
                <Alert severity={'error'}>This opportunity has been deleted.</Alert>
            )}

            {me.email !== owner && (
                <Alert 
                    severity={'error'}
                    action={
                        <Button color='inherit' size='small' sx={{ textTransform: 'none' }} onClick={onTakeOwnership}>
                            Take Ownership
                        </Button>
                    }
                >This opportunity is currently owned by {owner}.</Alert>
            )}

            <Box sx={{ paddingTop: '24px', paddingLeft: '24px', paddingRight: '24px', paddingBottom: '12px', maxWidth: '100%', margin: 'auto', background: theme.palette.background.default, pr: '10%', pl: '10%' }}>
                <Box sx={{ 
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                }}>
                    <Typography
                        component="span"
                        onClick={() => {
                            if (activeStep !== 1 && onPrevStep) onPrevStep();
                        }}
                        sx={{
                            fontSize: '13px',
                            fontWeight: 500,
                            color: activeStep === 1 ? theme.palette.text.disabled : theme.palette.primary.main,
                            cursor: activeStep === 1 ? 'default' : 'pointer',
                            flexShrink: 0,
                            '&:hover': activeStep !== 1 ? { textDecoration: 'underline' } : {}
                        }}
                    >
                        Back
                    </Typography>
                    <Box 
                        onClick={() => {
                            if (activeStep !== 1 && onPrevStep) {
                                onPrevStep();
                            }
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 16px',
                            borderRadius: '20px',
                            cursor: activeStep !== 1 ? 'pointer' : 'default',
                            backgroundColor: activeStep === 1 ? '#ffffff' : 'transparent',
                            boxShadow: activeStep === 1 ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none',
                            transition: 'all 0.2s ease',
                            '&:hover': activeStep !== 1 ? {
                                backgroundColor: '#f1f5f9',
                            } : {}
                        }}
                    >
                        <Box sx={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: activeStep === 1 ? theme.palette.primary.main : theme.palette.text.disabled,
                            marginRight: '8px',
                            flexShrink: 0
                        }}>
                            <Typography sx={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: activeStep === 1 ? '#ffffff' : theme.palette.text.secondary
                            }}>
                                1
                            </Typography>
                        </Box>
                        <Typography sx={{
                            fontSize: '13px',
                            fontWeight: activeStep === 1 ? 500 : 400,
                            color: activeStep === 1 ? theme.palette.text.primary : theme.palette.text.secondary
                        }}>
                            Offer Builder
                        </Typography>
                    </Box>
                    <Box 
                        onClick={() => {
                            if (activeStep !== 2 && !onNextDisabled && onNextStep) {
                                onNextStep();
                            }
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 16px',
                            borderRadius: '20px',
                            cursor: activeStep !== 2 && !onNextDisabled && onNextStep ? 'pointer' : 'default',
                            backgroundColor: activeStep === 2 ? '#ffffff' : 'transparent',
                            boxShadow: activeStep === 2 ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none',
                            transition: 'all 0.2s ease',
                            '&:hover': activeStep !== 2 && !onNextDisabled && onNextStep ? {
                                backgroundColor: '#f1f5f9',
                            } : {}
                        }}
                    >
                        <Box sx={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: activeStep === 2 ? theme.palette.primary.main : theme.palette.text.disabled,
                            marginRight: '8px',
                            flexShrink: 0
                        }}>
                            <Typography sx={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: activeStep === 2 ? '#ffffff' : theme.palette.text.secondary
                            }}>
                                2
                            </Typography>
                        </Box>
                        <Typography sx={{
                            fontSize: '13px',
                            fontWeight: activeStep === 2 ? 500 : 400,
                            color: activeStep === 2 ? theme.palette.text.primary : theme.palette.text.secondary
                        }}>
                            Preview & Send
                        </Typography>
                    </Box>
                    <Typography
                        component="span"
                        onClick={() => {
                            if (activeStep !== 2 && onNextStep) onNextStep();
                        }}
                        sx={{
                            fontSize: '13px',
                            fontWeight: 500,
                            color: activeStep === 2 ? theme.palette.text.disabled : theme.palette.primary.main,
                            cursor: activeStep === 2 ? 'default' : 'pointer',
                            flexShrink: 0,
                            '&:hover': activeStep !== 2 ? { textDecoration: 'underline' } : {}
                        }}
                    >
                        Next
                    </Typography>
                </Box>
            </Box>

            {showFollowUp && (
                <FollowUpDialog
                    opportunity={opportunity}
                    org={org}
                    me={me}
                    accounts={accounts}
                    accesstoken={accesstoken}
                    emailLoginInfo={emailLoginInfo}
                    onEmailRefreshConnection={onEmailRefreshConnection}
                    onEmailLogout={onEmailLogout}
                    onUpdateOpportunity={onUpdateOpportunity}
                    onDone={(text) => {
                        setShowFollowUp(false);
                        setFollowUpToast(text);
                    }}
                    onClose={() => setShowFollowUp(false)}
                />
            )}
            <Snackbar open={Boolean(errorToast)} autoHideDuration={8000} onClose={() => setErrorToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setErrorToast(null)} severity='error' variant='filled' sx={{ width: '100%' }}>{errorToast}</Alert>
            </Snackbar>
            <Snackbar open={Boolean(followUpToast)} autoHideDuration={5000} onClose={() => setFollowUpToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setFollowUpToast(null)} severity='success' variant='filled' sx={{ width: '100%' }}>{followUpToast}</Alert>
            </Snackbar>
            {status === 'sending-to-endcustomer' && (
                <Alert severity='info' icon={<CircularProgress size={20} />}
                    sx={{ borderRadius: '12px', fontSize: '13px', fontWeight: 500, alignItems: 'center' }}>
                    Sending quote to <b>{name(account)}{getRecipientAddresses(toRecipients).length > 0 ? ` (${joinWithAnd(getRecipientAddresses(toRecipients))})` : ''}</b>
                    {ccRecipients.length > 0 && (
                        <Tooltip title={'CC: ' + joinWithAnd(getRecipientAddresses(ccRecipients))}>
                            <FontAwesomeIcon icon={faCircleQuestion} style={{ verticalAlign: 'super', fontSize: '0.8em', cursor: 'pointer', marginLeft: '4px' }} />
                        </Tooltip>
                    )}...
                </Alert>
            )}
            {status === 'sent-to-endcustomer' && (
                <Alert severity='info'
                    action={<Box sx={{ display: 'flex', gap: '4px' }}>
                        <Button size='small' sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 400 }} onClick={() => setShowFollowUp(true)}>Follow-up</Button>
                        <Button size='small' sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600 }} onClick={onReopen}>Reopen</Button>
                    </Box>}
                    sx={{ borderRadius: '12px', fontSize: '13px', fontWeight: 500, alignItems: 'center' }}>
                    Quote sent to <b>{name(account)}{getRecipientAddresses(toRecipients).length > 0 ? ` (${joinWithAnd(getRecipientAddresses(toRecipients))})` : ''}</b>
                    {ccRecipients.length > 0 && (
                        <Tooltip title={'CC: ' + joinWithAnd(getRecipientAddresses(ccRecipients))}>
                            <FontAwesomeIcon icon={faCircleQuestion} style={{ verticalAlign: 'super', fontSize: '0.8em', cursor: 'pointer', marginLeft: '4px' }} />
                        </Tooltip>
                    )}
                </Alert>
            )}
            {status === 'opened-by-customer' && (
                <Alert severity='info'
                    action={<Box sx={{ display: 'flex', gap: '4px' }}>
                        <Button size='small' sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 400 }} onClick={() => setShowFollowUp(true)}>Follow-up</Button>
                        <Button size='small' sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600 }} onClick={onReopen}>Reopen</Button>
                    </Box>}
                    sx={{ borderRadius: '12px', fontSize: '13px', fontWeight: 500, alignItems: 'center' }}>
                    Quote viewed by <b>{name(account)}{getRecipientAddresses(toRecipients).length > 0 ? ` (${joinWithAnd(getRecipientAddresses(toRecipients))})` : ''}</b>
                    {ccRecipients.length > 0 && (
                        <Tooltip title={'CC: ' + joinWithAnd(getRecipientAddresses(ccRecipients))}>
                            <FontAwesomeIcon icon={faCircleQuestion} style={{ verticalAlign: 'super', fontSize: '0.8em', cursor: 'pointer', marginLeft: '4px' }} />
                        </Tooltip>
                    )}
                </Alert>
            )}
            {status === 'completed' && (
                <Alert severity='success'
                    action={<Button size='small' sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600 }} onClick={onReopen}>Reopen</Button>}
                    sx={{ borderRadius: '12px', fontSize: '13px', fontWeight: 500, alignItems: 'center' }}>
                    {(() => {
                        if (_.isEmpty(opportunity?.offers)) return 'Accepted ';
                        if (opportunity.offers.length === 1) return 'Accepted ';
                        const offerIndex = opportunity.offers.findIndex(o => opportunity.selected?.includes(o.id));
                        if (offerIndex === -1) return 'Accepted ';
                        if (!opportunity.offers[offerIndex].title) return `Offer #${offerIndex + 1} accepted `;
                        return `Offer "${opportunity.offers[offerIndex].title}" accepted `;
                    })()}
                    {(() => {
                        const validRecipients = toRecipients.filter(r => r.address !== DUMMY_CUSTOMER_EMAIL);
                        if (!_.isEmpty(validRecipients)) return <>by <b>{joinWithAnd(validRecipients.map(r => r.address))}</b></>;
                        return '';
                    })()} — marked as Won
                </Alert>
            )}
            {status === 'lost' && (
                <Alert severity='error'
                    action={<Button size='small' sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600 }} onClick={onReopen}>Reopen</Button>}
                    sx={{ borderRadius: '12px', fontSize: '13px', fontWeight: 500, alignItems: 'center' }}>
                    {opportunity.lostReason ? (
                        <>Marked as Lost by <b>{opportunity.lostReason.by || 'a team member'}</b> — {lostReasonText(opportunity.lostReason)}</>
                    ) : (
                        <>Quote declined by {(rejection || {}).modifiedBy || "the customer"}{rejection && rejection.reason ? ` — "${rejection.reason}"` : ''} — marked as Lost</>
                    )}
                </Alert>
            )}
            <LostReasonDialog
                open={askLostReason}
                working={markingLost}
                onClose={() => setAskLostReason(false)}
                onConfirm={onMarkLost}
                offers={opportunity.offers}
            />
            {status === 'endcustomer-accepted' && (
                <Alert severity='success'
                    action={<Tooltip title={canChangeStatus ? '' : `Only the quote's owner${owner ? ` (${owner})` : ''} can mark it as Won`}><span><Button size='small' variant='outlined' color='success' onClick={onMarkCompleted} disabled={markingCompleted || !canChangeStatus} sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600 }}>
                        {!markingCompleted ? 'Mark as Won' : <CircularProgress size={14} />}
                    </Button></span></Tooltip>}
                    sx={{ borderRadius: '12px', fontSize: '13px', fontWeight: 500, alignItems: 'center' }}>
                    {(() => {
                        if (_.isEmpty(opportunity?.offers)) return 'Accepted ';
                        if (opportunity.offers.length === 1) return 'Accepted ';
                        const offerIndex = opportunity.offers.findIndex(o => opportunity.selected?.includes(o.id));
                        if (offerIndex === -1) return 'Accepted ';
                        if (!opportunity.offers[offerIndex].title) return `Offer #${offerIndex + 1} accepted `;
                        return `Offer "${opportunity.offers[offerIndex].title}" accepted `;
                    })()}
                    {(() => {
                        const validRecipients = toRecipients.filter(r => r.address !== DUMMY_CUSTOMER_EMAIL);
                        if (!_.isEmpty(validRecipients)) return <>by <b>{name(account)} ({joinWithAnd(validRecipients.map(r => r.address))})</b></>;
                        return <>by <b>{name(account)}</b></>;
                    })()}
                </Alert>
            )}
            {status === 'endcustomer-rejected' && (
                <Alert severity='error'
                    action={<Tooltip title={canChangeStatus ? '' : `Only the quote's owner${owner ? ` (${owner})` : ''} can mark it as Lost`}><span><Button size='small' variant='outlined' color='error' onClick={() => setAskLostReason(true)} disabled={markingLost || !canChangeStatus} sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600 }}>
                        {!markingLost ? 'Mark as Lost' : <CircularProgress size={14} />}
                    </Button></span></Tooltip>}
                    sx={{ borderRadius: '12px', fontSize: '13px', fontWeight: 500, alignItems: 'center' }}>
                    Quote declined by <b>{rejection?.modifiedBy || 'customer'}</b>{rejection?.reason ? <> — "{rejection.reason}"</> : ''}
                </Alert>
            )}
            <Drawer
                open={showThread}
                onClose={() => setShowThread(false)}
                anchor='right'
            >
                {foundThread && foundThread.length && (
                    <EmailConversation
                        conversation={foundThread}
                        organization={org}
                        tokens={emailLoginInfo.tokens}
                        accesstoken={accesstoken}
                        emailLoginInfo={emailLoginInfo}
                        noControls={true}
                    />
                )}
            </Drawer>
            {changeOwnership && (
                <ChangeOwnershipDialog
                    onClose={() => setChangeOwnership(false)}
                    org={org}
                    opportunity={opportunity}
                    onUpdateOpportunity={onUpdateOpportunity}
                    me={me}
                    accesstoken={accesstoken}
                />
            )}
            {advancedSettingsOpen && (
                <AdvancedSettingsDialog
                    open={true}
                    onClose={() => setAdvancedSettingsOpen(false)}
                    opportunity={opportunity}
                    onUpdateOpportunity={onUpdateOpportunity}
                    opportunityId={opportunity?.id}
                />
            )}
        </>
    );
}

function ChangeOwnershipDialog(props) {
    const { onClose, org, onUpdateOpportunity, opportunity, me, accesstoken } = props;
    // Default to the first org user, but never assume the list is populated: arriving via a
    // cross-workspace notification can render this during an org (re)load window where
    // org.users is momentarily empty, and this expression is re-evaluated on every render.
    const [ newOwner, setNewOwner ] = useState(org.users?.[0]?.email || opportunity?.owner || me?.email || '');
    const [ saving, setSaving ] = useState(false);
    const [ error, setError ] = useState(null);

    async function onSave() {
        setSaving(true);
        try {
            const { data } = await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${opportunity.organizationId}&accesstoken=${accesstoken}`, {
                owner: newOwner,
            });
            onUpdateOpportunity({ opportunity: { ...opportunity, ...data } });
            onClose();
        } catch (e) {
            setError(e.response?.data?.message || 'Error changing ownership');
        }
        setSaving(false);
    }

    return (
        <Dialog open={true} onClose={onClose}>
            <DialogTitle>Change Ownership</DialogTitle>
            <DialogContent>
                <Typography variant='body1' sx={{ mt: 2, mb: 2 }}>
                    Please select the new owner for this opportunity:
                </Typography>
                <Alert severity='warning' sx={{ mt: 2, mb: 2 }}>
                    Once the ownership is changed, the new owner will have full control over this opportunity, and you will no longer be able to modify it.
                </Alert>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
                    <OwnerFilter
                        org={org}
                        filter={{ members: [newOwner] }}
                        setFilter={(filter) => setNewOwner(filter.members[0])}
                        me={me}
                        singlePick={true}
                        disabled={false}
                        teamLevel={false}
                        noSelectionText={null}
                        noMarginRight={false}
                    />
                </Box>
                {error && (
                    <Alert severity='error' sx={{ mt: 2, mb: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button onClick={onSave} disabled={saving}>
                    {saving ? <CircularProgress size={14} /> : 'Change Ownership'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default OpportunityHeader;

function name(account) {
    if (!account) return 'Unknown';
    if (account.firstName) {
        return `${account.firstName} ${account.lastName}`;
    }
    return account.name || 'Unknown';
}

const joinWithAnd = arr => arr.length <= 2 ? arr.join(' and ') : `${arr.slice(0, -1).join(', ')} and ${arr.at(-1)}`;

// Helper to extract email address from recipient (handles both string and object formats)
const getRecipientAddress = r => {
    if (!r) return null;
    if (typeof r === 'string') return r;
    return r.address || r.email || null;
};

const getRecipientAddresses = recipients => (recipients || []).map(getRecipientAddress).filter(Boolean);